import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown, Facebook } from 'lucide-react';
import { Button } from './ui/Button';
export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  // Handle scroll effect for sticky header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close mobile menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
    setIsServicesOpen(false);
  }, [location]);
  const navLinks = [{
    name: 'Home',
    path: '/'
  }, {
    name: 'About',
    path: '/about'
  }];
  const services = [{
    name: 'Full Bathroom Remodeling',
    path: '/services/full-bathroom-remodeling'
  }, {
    name: 'Tub-to-Shower Conversions',
    path: '/services/tub-to-shower-conversion'
  }, {
    name: 'Custom Tile Work',
    path: '/services/custom-tile-work'
  }, {
    name: 'Vanity Installation',
    path: '/services/vanity-installation'
  }, {
    name: 'Accessibility Upgrades',
    path: '/services/accessibility-upgrades'
  }, {
    name: 'Flooring & Lighting',
    path: '/services/flooring-and-lighting'
  }];
  return <header className={`fixed left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm py-1 top-0' : 'bg-white py-1.5 top-10'}`} data-id="element-110">
      <div className="container mx-auto px-4 md:px-6" data-id="element-111">
        <div className="flex items-center justify-between gap-6" data-id="element-112">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-id="element-113">
            <img src="https://cdn.magicpatterns.com/uploads/1TFbdJ5eV3nzr3e69GzZoi/logo.png" alt="Lucky Duck Bathroom Company Logo" className="h-20 w-auto transition-transform group-hover:scale-105" data-id="element-114" />
          </Link>

          {/* Centered Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8 absolute left-1/2 -translate-x-1/2" data-id="element-115">
            {navLinks.map(link => <Link key={link.path} to={link.path} className={`text-sm font-medium transition-colors hover:text-[#1E3A5F] ${location.pathname === link.path ? 'text-[#1E3A5F]' : 'text-gray-600'}`} data-id="element-116">
                {link.name}
              </Link>)}

            {/* Services Dropdown */}
            <div className="relative group" data-id="element-117">
              <button className="flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-[#1E3A5F] transition-colors" onMouseEnter={() => setIsServicesOpen(true)} onMouseLeave={() => setIsServicesOpen(false)} data-id="element-118">
                Our Services
                <ChevronDown className="h-4 w-4" data-id="element-119" />
              </button>

              {/* Dropdown Menu */}
              <div className={`absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-100 py-2 transition-all duration-200 ${isServicesOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'}`} onMouseEnter={() => setIsServicesOpen(true)} onMouseLeave={() => setIsServicesOpen(false)} data-id="element-120">
                {services.map((service, index) => <Link key={index} to={service.path} className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#8BA888]/10 hover:text-[#1E3A5F] transition-colors" data-id="element-121">
                    {service.name}
                  </Link>)}
              </div>
            </div>

            <Link to="/faq" className={`text-sm font-medium transition-colors hover:text-[#1E3A5F] ${location.pathname === '/faq' ? 'text-[#1E3A5F]' : 'text-gray-600'}`} data-id="element-122">
              FAQ
            </Link>

            <Link to="/contact" className={`text-sm font-medium transition-colors hover:text-[#1E3A5F] ${location.pathname === '/contact' ? 'text-[#1E3A5F]' : 'text-gray-600'}`} data-id="element-123">
              Contact
            </Link>
          </nav>

          {/* Right Side Actions */}
          <div className="hidden md:flex items-center gap-3" data-id="element-124">
            <a href="https://www.facebook.com/luckyduckbath" target="_blank" rel="noopener noreferrer" className="p-2 text-gray-600 hover:text-[#1E3A5F] transition-colors" aria-label="Visit our Facebook page" data-id="element-125">
              <Facebook className="h-5 w-5" data-id="element-126" />
            </a>
            <Button variant="primary" size="sm" className="gap-2" data-id="element-127">
              <Phone className="h-4 w-4" data-id="element-128" />
              <span data-id="element-129">(574) 914-1522</span>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button className="md:hidden p-2 text-gray-600 hover:text-[#8BA888] transition-colors" onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Toggle menu" data-id="element-130">
            {isMenuOpen ? <X className="h-6 w-6" data-id="element-131" /> : <Menu className="h-6 w-6" data-id="element-132" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && <div className="md:hidden absolute top-full left-0 right-0 bg-white border-t border-gray-100 shadow-lg p-4 flex flex-col gap-4 animate-in slide-in-from-top-5 duration-200" data-id="element-133">
          {navLinks.map(link => <Link key={link.path} to={link.path} className={`text-lg font-medium py-2 px-4 rounded-lg hover:bg-gray-50 ${location.pathname === link.path ? 'text-[#1E3A5F] bg-gray-50' : 'text-gray-600'}`} data-id="element-134">
              {link.name}
            </Link>)}

          {/* Mobile Services Accordion */}
          <div className="border-t border-gray-100 pt-4" data-id="element-135">
            <button onClick={() => setIsServicesOpen(!isServicesOpen)} className="flex items-center justify-between w-full text-lg font-medium py-2 px-4 rounded-lg hover:bg-gray-50 text-gray-600" data-id="element-136">
              Our Services
              <ChevronDown className={`h-5 w-5 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} data-id="element-137" />
            </button>

            {isServicesOpen && <div className="mt-2 ml-4 space-y-2" data-id="element-138">
                {services.map((service, index) => <Link key={index} to={service.path} className="block text-sm text-gray-600 py-2 px-4 hover:text-[#1E3A5F] transition-colors" data-id="element-139">
                    {service.name}
                  </Link>)}
              </div>}
          </div>

          <Link to="/faq" className={`text-lg font-medium py-2 px-4 rounded-lg hover:bg-gray-50 ${location.pathname === '/faq' ? 'text-[#1E3A5F] bg-gray-50' : 'text-gray-600'}`} data-id="element-140">
            FAQ
          </Link>

          <Link to="/contact" className={`text-lg font-medium py-2 px-4 rounded-lg hover:bg-gray-50 ${location.pathname === '/contact' ? 'text-[#1E3A5F] bg-gray-50' : 'text-gray-600'}`} data-id="element-141">
            Contact
          </Link>

          <div className="pt-2 border-t border-gray-100 space-y-3" data-id="element-142">
            <a href="https://www.facebook.com/luckyduckbath" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-600 hover:text-[#1E3A5F] py-2 px-4" data-id="element-143">
              <Facebook className="h-5 w-5" data-id="element-144" />
              <span data-id="element-145">Follow us on Facebook</span>
            </a>
            <Button variant="primary" className="w-full gap-2 justify-center" data-id="element-146">
              <Phone className="h-4 w-4" data-id="element-147" />
              <span data-id="element-148">Call Now</span>
            </Button>
          </div>
        </div>}
    </header>;
}